import styled from 'styled-components';
import Lottie from 'lottie-react';

import Image from '@components/image';
import {
  SHOW_CARD_WIDTH,
  SHOW_CARD_HEIGHT,
  SKEW_ANGLE,
} from '@utils/constants';
import { PremierText } from '@components/text';
import TouchableOpacity from '@components/buttons/TouchableOpacity';
import BaseImagePlaceholder from '@components/loader/ImagePlaceholder';

export const Container = styled.div<{ $disabled?: boolean }>`
  width: ${SHOW_CARD_WIDTH}px;
  align-items: center;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
  }
`;

export const ImageContainer = styled.div`
  position: relative;
  width: ${SHOW_CARD_WIDTH}px;
  height: ${SHOW_CARD_HEIGHT}px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
    aspect-ratio: 2 / 1; /* 2:1 aspect ratio (width:height) */
  }
`;

export const ShowImage = styled(Image)`
  position: absolute;
  width: ${SHOW_CARD_WIDTH}px;
  height: ${SHOW_CARD_HEIGHT}px;
  border: 0.5px solid ${({ theme }) => theme.colors.grey[600]};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
  }
`;

export const ImagePlaceholder = styled(BaseImagePlaceholder)`
  width: ${SHOW_CARD_WIDTH}px;
  height: ${SHOW_CARD_HEIGHT}px;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
    height: 100%;
  }
`;

export const CountdownContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const TopImageContainerPart = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  padding: 8px;
`;

export const ParticipantsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

export const LiveNow = styled(Lottie)`
  width: 48px;
  height: 48px;
  margin-right: -8px;
  margin-top: -8px;
`;

export const RedCircle = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 4px;
  background-color: ${({ theme }) => theme.colors.red[500]};
`;

export const RecText = styled(PremierText)`
  font-size: ${({ theme }) => theme.fonts.fontSize.xs};
  color: ${({ theme }) => theme.colors.white};
  transform: skew(${SKEW_ANGLE}deg);
`;

export const BellButton = styled(TouchableOpacity)<{ $isSubscribed?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px;
  background: ${({ theme, $isSubscribed }) =>
    $isSubscribed
      ? `linear-gradient(
    120deg,
    ${theme.colors.red[500]} 0%,
    ${theme.colors.purple[500]} 50%,
    ${theme.colors.blue[500]} 100%
    )`
      : `${theme.colors.black}${theme.opacity[60]}`};
  border-radius: 4px;
  border: none;
  z-index: 1;
`;

export const BottomImageContainerPart = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  padding: 8px;
`;

export const HostsContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

export const HostAvatarWrapper = styled.div`
  margin-top: 12px;
  margin-left: 8px;
  z-index: 1;
`;

export const CoHostAvatarWrapper = styled.div`
  margin-left: -8px;
  z-index: 0;
`;

export const TitleWithChannelContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 8px;
`;

export const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

export const TitleContainer = styled.div`
  flex: 1;
  margin-left: 12px;
`;

export const TitleText = styled.h3`
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fonts.fontSize.sm};
  font-weight: ${({ theme }) => theme.fonts.fontWeight.bold};
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SubTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const HostNameContainer = styled.div<{ $isCoHost: boolean }>`
  flex-grow: 0;
  flex-shrink: 1;
  flex-basis: auto;
  max-width: ${({ $isCoHost }) => ($isCoHost ? '50%' : '100%')};
`;

export const CoHostNameContainer = styled.div`
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 0;
`;

export const NamesDivider = styled.span`
  margin-inline: 6px;
  color: ${({ theme }) => theme.colors.grey[400]};
  font-size: ${({ theme }) => theme.fonts.fontSize.xs};
`;

export const InfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`;

export const MainInfoContainer = styled(InfoContainer)`
  margin-top: 8px;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding-inline: 8px;
  }
`;

export const Ellipse = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.grey[400]};
  margin: 0 8px;
`;

export const SubTitleText = styled.span`
  color: ${({ theme }) => theme.colors.grey[400]};
  font-size: ${({ theme }) => theme.fonts.fontSize.xs};
`;

export const ChannelButton = styled(TouchableOpacity)`
  position: relative;
  width: 36px;
  height: 36px;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  margin-left: 4px;
`;

export const ChannelImagePlaceholder = styled(BaseImagePlaceholder)`
  width: 36px;
  height: 36px;
`;

export const ChannelImage = styled(Image)`
  border: 0.5px solid ${({ theme }) => theme.colors.grey[600]};
`;
